// Bring-your-own-song planning — the mechanical on-ramp: analyze an uploaded MIDI and
// route the shortest path to the curriculum node it reaches. Pure (the UI wires a file
// → this → a path panel). Fully deterministic + offline: key/chords (MIR) + derived shapes
// (chord quality) + progressions (chord sequence) + LH texture (figuration classifier).
// Difficulty is estimated via the tag fallback (gradeFromTags, through difficulty({uses,key}));
// wiring through extractFeatures→grade(features) in-browser is a follow-up. The GPU/ML judge
// is permanently retired — grade(features) is the shipped catalog grader.
// See docs/architecture.md → "Bring-your-own-song (upload)".

import type { NoteEvent } from "../types";
import { chordShapeSkills, progressionSkills } from "./arranger";
import type { CatalogEntry } from "./catalog";
import { chordFromAtom } from "./chord-atoms";
import { difficulty } from "./difficulty";
import { classifyTexture } from "./figuration";
import { analyzeMidi, barChordAtoms } from "./mir";
import { pathTo } from "./pathfind";
import type { LatticeNode, SongLattice } from "./song-lattice";

// Line-of-fifths → major-key tonic name (single-char accidental, as progressionSkills parses).
// progressionSkills only fires in major, so a minor upload (sharing fifths with its relative
// major) is read in the major frame and harmlessly returns [].
const MAJOR_KEY_BY_FIFTHS: Record<number, string> = {
  0: "C",
  1: "G",
  2: "D",
  3: "A",
  4: "E",
  5: "B",
  6: "F#",
  7: "C#",
  [-1]: "F",
  [-2]: "Bb",
  [-3]: "Eb",
  [-4]: "Ab",
  [-5]: "Db",
  [-6]: "Gb",
  [-7]: "Cb",
};

export interface UploadPlan {
  /** Inferred key (line-of-fifths position). */
  keyFifths: number;
  /** Inferred major-key name (display + progression frame). */
  key: string;
  /** Chord atoms the upload uses (deterministic MIR), deduped. */
  chords: string[];
  /** Triad-shape skills the chords imply (`triad-major/minor`, `dom7`). */
  shapes: string[];
  /** Recognized chord-loop progressions in the bar sequence (`prog-*`). */
  progressions: string[];
  /** Dominant LH accompaniment texture(s) (figuration classifier), or [] if unsure. */
  texture: string[];
  /** Estimated difficulty via the tag fallback (gradeFromTags); extractFeatures→grade() wiring is a follow-up. */
  difficulty: number;
  /** Chord atoms the curriculum has no drill for — out of the current vocabulary. */
  beyond: string[];
  /** The lattice node the upload reaches (the deepest one its vocabulary subsumes). */
  target: LatticeNode | null;
  /** Ordered rungs from the unlocked frontier to `target`, or null if unreachable. */
  path: LatticeNode[] | null;
}

/**
 * Plan how to reach an uploaded song against a prebuilt lattice. The upload's derived
 * vocabulary is `five-finger` (the floor) ∪ chord atoms ∪ derived shapes ∪ progressions ∪
 * classified texture; the **target** is the deepest existing node whose requirements that
 * vocabulary fully covers, and `pathTo` routes to it. Chords with no drill are reported in
 * `beyond` (the route-and-flag / future auto-extend signal). All deterministic, no network.
 */
export function planUpload(
  notes: readonly NoteEvent[],
  bars: readonly number[],
  lattice: SongLattice,
  drilledAtoms: ReadonlySet<string>,
): UploadPlan {
  const { keyFifths, chords } = analyzeMidi(notes, bars);
  const beyond = chords.filter((c) => !drilledAtoms.has(c));
  const key = `${MAJOR_KEY_BY_FIFTHS[keyFifths] ?? "C"} major`;

  // Reconstruct DrillChords (octave-agnostic) for the derived skills.
  const dedupChords = chords.map(chordFromAtom).filter((c) => c !== null);
  const seqChords = barChordAtoms(notes, bars)
    .map(chordFromAtom)
    .filter((c) => c !== null);
  const shapes = chordShapeSkills(dedupChords);
  const progressions = progressionSkills(seqChords, key);
  const texture = classifyTexture(notes, bars);

  const uses = [...new Set<string>([...chords, ...shapes, ...progressions, ...texture])];
  const vocab = new Set<string>(["five-finger", ...uses]);
  let target: LatticeNode | null = null;
  for (const node of lattice.nodes) {
    const reach = node.requirements.every((r) => vocab.has(r));
    if (reach && node.requirements.length > (target?.requirements.length ?? -1)) target = node;
  }

  const path = target ? pathTo(lattice, target.id) : null;
  const diff = difficulty({ uses, key } as CatalogEntry);
  return {
    keyFifths,
    key,
    chords,
    shapes,
    progressions,
    texture,
    difficulty: diff,
    beyond,
    target,
    path,
  };
}
